import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Briefcase, DollarSign, Users, MapPin, RefreshCw, Filter, ChevronDown, ChevronUp, Info } from 'lucide-react';

const EmploymentDashboard = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employmentData, setEmploymentData] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [showLayoffs, setShowLayoffs] = useState(true);
  const [showHiring, setShowHiring] = useState(true);

  // Fetch employment data from API
  useEffect(() => {
    fetchEmploymentData();
  }, []);

  const fetchEmploymentData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/app/api/employment-data.php');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setEmploymentData(result.data);
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to fetch employment data');
      }
    } catch (err) {
      console.error('Error fetching employment data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const formatNumber = (num) => {
    if (!num) return 'N/A';
    const value = parseFloat(num);
    if (isNaN(value)) return 'N/A';
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    const value = parseFloat(amount);
    if (isNaN(value)) return 'N/A';
    return `$${value.toLocaleString()}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const num = parseFloat(value);
    if (isNaN(num)) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  const getTrendIcon = (value) => {
    if (!value) return null;
    if (value > 0) return <TrendingUp className="h-5 w-5 text-red-600" />;
    if (value < 0) return <TrendingDown className="h-5 w-5 text-green-600" />;
    return null;
  };

  const getRiskColor = (score) => {
    if (!score) return 'bg-gray-100 border-gray-300 text-gray-600';
    if (score >= 75) return 'bg-red-100 border-red-300 text-red-700';
    if (score >= 50) return 'bg-orange-100 border-orange-300 text-orange-700';
    if (score >= 25) return 'bg-yellow-100 border-yellow-300 text-yellow-700';
    return 'bg-green-100 border-green-300 text-green-700';
  };

  const getLayoffTrendColor = (trend) => {
    if (trend === 'accelerating') return 'text-red-600';
    if (trend === 'declining') return 'text-green-600';
    return 'text-gray-600';
  };

  const getHiringVelocityColor = (velocity) => {
    if (velocity === 'strong') return 'text-green-600';
    if (velocity === 'contracting') return 'text-red-600';
    if (velocity === 'weak') return 'text-orange-600';
    return 'text-gray-600';
  };

  // Filter industry data
  const filteredIndustryData = employmentData?.industryData?.filter(industry => {
    if (selectedIndustry === 'all') return true;
    return industry.industry_category === selectedIndustry;
  }) || [];

  // Get unique industries for filter
  const industries = ['all', ...new Set(employmentData?.industryData?.map(i => i.industry_category) || [])];

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading employment data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-300 rounded-lg p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-900 mb-2">Error Loading Data</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchEmploymentData}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const national = employmentData?.nationalTrends;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Employment Data Intelligence</h1>
              <p className="text-gray-600">
                Data for {employmentData?.dataMonth || 'December 2025'} • Last updated: {employmentData?.lastUpdated}
              </p>
            </div>
            <button
              onClick={fetchEmploymentData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* National Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Net Job Change */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Net Job Change</p>
              {getTrendIcon(national?.net_change)}
            </div>
            <p className={`text-3xl font-bold ${national?.net_change < 0 ? 'text-red-600' : 'text-green-600'}`}>
              {national?.net_change > 0 ? '+' : ''}{formatNumber(national?.net_change)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {formatNumber(national?.jobs_added)} added, {formatNumber(national?.jobs_lost)} lost
            </p>
          </div>

          {/* Unemployment Rate */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Unemployment Rate</p>
              <Users className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {formatPercent(national?.unemployment_rate)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {formatNumber(national?.total_unemployed)} unemployed
            </p>
          </div>

          {/* Labor Force Participation */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Labor Force Participation</p>
              <Briefcase className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {formatPercent(national?.labor_force_participation_rate)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {formatNumber(national?.total_labor_force)} total
            </p>
          </div>

          {/* Real Wage Growth */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Real Wage Growth</p>
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <p className={`text-3xl font-bold ${national?.real_wage_growth_pct < 0 ? 'text-red-600' : 'text-green-600'}`}>
              {national?.real_wage_growth_pct > 0 ? '+' : ''}{formatPercent(national?.real_wage_growth_pct)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {formatCurrency(national?.avg_hourly_wage)}/hour avg
            </p>
          </div>
        </div>

        {/* Methodology Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <strong>Data Sources:</strong> {national?.data_source || 'BLS, ADP, Challenger Gray & Christmas, State Labor Departments'}. 
              Updated {employmentData?.dataMonth}. Real wage growth is inflation-adjusted. 
              Composite risk scores calculated from automation potential (40%), layoff trends (30%), and hiring velocity (30%).
            </div>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <span className="font-semibold text-gray-900">Filters:</span>
            </div>
            
            <select
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {industries.map(ind => (
                <option key={ind} value={ind}>
                  {ind === 'all' ? 'All Industries' : ind.charAt(0).toUpperCase() + ind.slice(1).replace('_', ' ')}
                </option>
              ))}
            </select>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showLayoffs}
                onChange={(e) => setShowLayoffs(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Show Layoff Data</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showHiring}
                onChange={(e) => setShowHiring(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Show Hiring Trends</span>
            </label>
          </div>
        </div>

        {/* Industry Data Cards */}
        <div className="space-y-4 mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Industry Employment Analysis</h2>
          
          {filteredIndustryData.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-8 text-center">
              <p className="text-gray-600">No industry data available for the selected filters.</p>
            </div>
          ) : (
            filteredIndustryData.map((industry, idx) => (
              <div key={idx} className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
                {/* Card Header */}
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedCard(expandedCard === idx ? null : idx)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{industry.industry_name}</h3>
                        <span className="text-sm text-gray-500">
                          {industry.industry_category?.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <span>{formatNumber(industry.total_employed)} employed</span>
                        <span>•</span>
                        <span className={industry.net_change < 0 ? 'text-red-600' : 'text-green-600'}>
                          {industry.net_change > 0 ? '+' : ''}{formatNumber(industry.net_change)} net change
                        </span>
                        <span>•</span>
                        <span>{formatCurrency(industry.avg_hourly_wage)}/hour</span>
                      </div>
                      
                      {/* Risk Score Badge */}
                      {industry.composite_risk_score && (
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-semibold ${getRiskColor(industry.composite_risk_score)}`}>
                          <AlertTriangle className="h-4 w-4" />
                          <span>Risk Score: {industry.composite_risk_score}/100</span>
                        </div>
                      )}
                    </div>

                    <button className="text-gray-400 hover:text-gray-600">
                      {expandedCard === idx ? (
                        <ChevronUp className="h-6 w-6" />
                      ) : (
                        <ChevronDown className="h-6 w-6" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedCard === idx && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <div className="grid md:grid-cols-3 gap-6">
                      
                      {/* Employment Metrics */}
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3">Employment Metrics</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Jobs Added:</span>
                            <span className="font-semibold text-green-600">+{formatNumber(industry.jobs_added)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Jobs Lost:</span>
                            <span className="font-semibold text-red-600">-{formatNumber(industry.jobs_lost)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Unemployment:</span>
                            <span className="font-semibold">{formatPercent(industry.unemployment_rate)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Wage Data */}
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3">Wage Trends</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Avg Hourly:</span>
                            <span className="font-semibold">{formatCurrency(industry.avg_hourly_wage)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">YoY Growth:</span>
                            <span className={`font-semibold ${industry.wage_growth_yoy_pct < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {industry.wage_growth_yoy_pct > 0 ? '+' : ''}{formatPercent(industry.wage_growth_yoy_pct)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Risk Indicators */}
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3">Risk Indicators</h4>
                        <div className="space-y-2 text-sm">
                          {industry.automation_risk_score && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Automation Risk:</span>
                              <span className="font-semibold">{industry.automation_risk_score}/100</span>
                            </div>
                          )}
                          {industry.layoff_trend && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Layoff Trend:</span>
                              <span className={`font-semibold capitalize ${getLayoffTrendColor(industry.layoff_trend)}`}>
                                {industry.layoff_trend}
                              </span>
                            </div>
                          )}
                          {industry.hiring_velocity && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Hiring Velocity:</span>
                              <span className={`font-semibold capitalize ${getHiringVelocityColor(industry.hiring_velocity)}`}>
                                {industry.hiring_velocity}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Recent Layoffs Section */}
        {showLayoffs && employmentData?.recentLayoffs && employmentData.recentLayoffs.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Recent Layoffs (Last 90 Days)</h2>
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Industry</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Affected</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">AI Related</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {employmentData.recentLayoffs.slice(0, 10).map((layoff, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(layoff.announcement_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">{layoff.company}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                          {layoff.industry_category?.replace('_', ' ')}
                        </td>
                        <td className="px-6 py-4 text-sm text-right font-semibold text-red-600">
                          {formatNumber(layoff.employees_affected)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{layoff.location_state || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm">
                          {layoff.replaced_by_ai ? (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
                              Yes
                            </span>
                          ) : (
                            <span className="text-gray-400">No</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {layoff.source_url ? (
                            <a 
                              href={layoff.source_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline"
                              title={layoff.source_title || 'View source'}
                            >
                              Link
                            </a>
                          ) : (
                            <span className="text-gray-400 text-xs">{layoff.data_source}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Layoff Summary by Industry */}
        {showLayoffs && employmentData?.layoffSummary && employmentData.layoffSummary.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Layoff Summary by Industry (Last 3 Months)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {employmentData.layoffSummary.map((summary, idx) => (
                <div key={idx} className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
                  <h3 className="font-bold text-gray-900 mb-3 capitalize">
                    {summary.industry_category?.replace('_', ' ')}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Affected:</span>
                      <span className="font-semibold text-red-600">
                        {formatNumber(summary.total_employees_affected)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Events:</span>
                      <span className="font-semibold">{summary.layoff_events}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">AI-Related:</span>
                      <span className="font-semibold text-red-600">
                        {formatNumber(summary.ai_related_layoffs)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 bg-gray-100 rounded-lg p-6 text-center">
          <p className="text-gray-700 mb-2">
            Employment data combines official government statistics (BLS, ADP, State Labor Departments) with 
            real-time intelligence from Challenger Gray & Christmas layoff reports and Under the Radar career research.
          </p>
          <p className="text-sm text-gray-600">
            Data updated monthly for official statistics, weekly for layoff tracking and hiring trends. 
            Last updated: {employmentData?.lastUpdated}
          </p>
        </div>

      </div>
    </div>
  );
};

export default EmploymentDashboard;